import React, { useEffect, useRef, useState } from "react";
import {
  PayPalScriptProvider,
  usePayPalCardFields,
  PayPalCardFieldsProvider,
  PayPalButtons,
  PayPalCardFieldsForm,
} from "@paypal/react-paypal-js";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ErrorMsg } from "../../utils/Toast";
import { urlBase } from "../../utils/API";
import "./paypal.css";

export default function PaypalAdvanced({
  productId,
  currency = "USD",
  country = "US",
  ...props
}) {
  const params = useParams();
  const navigate = useNavigate();
  const loggedUser = useSelector((state) => state.loggedUser);

  const [isPaying, setIsPaying] = useState(false);
  const [message, setMessage] = useState("");
  const requestId = useRef(null);
  const [userHasSubscription, setUserHasSubscription] = useState(false);

  const initialOptions = {
    "client-id": "Aa6mfTWPBfn-A8Ap55uxXrgonuKfo9jHRQwK2CXIVvAARbv0KFHlsyAaRedsFHo7U-k3yFgwbr6raWKt",
    "enable-funding": "card,venmo",
    currency: currency,
    components: "buttons,card-fields",
    intent: "capture",
  };

  useEffect(() => {
    if (requestId.current) {
      console.log("RequestId Updated:", requestId.current);
    }
  }, [requestId]);

  const createOrder = async () => {
    try {
      const response = await fetch(
        `${urlBase}/api/create/paypal/payment-advanced?userId=${loggedUser?.user?._id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            packageId: params.packageId || props?.packageId,
            classId: params.classId || props?.classId,
          }),
        }
      );

      const orderData = await response.json();
      if (orderData?.error) {
        setUserHasSubscription(true);
        setMessage(orderData?.error);
        alert(orderData?.error);
        return null;
      }

      console.log("Order created:", orderData);
      requestId.current = orderData?.requestId;
      return orderData?.id;
    } catch (error) {
      console.error("Error creating order:", error);
      ErrorMsg(error?.message || "Failed to create order");
      return null;
    }
  };

  const onApprove = async (data) => {
    const controller = new AbortController();
    const signal = controller.signal;
  
    try {
      if (!data.orderID) return;
  
      const response = await fetch(
        `${urlBase}/api/orders/${data.orderID}/capture?userId=${loggedUser?.user?._id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            packageId: params.packageId || props?.packageId,
            classId: params.classId || props?.classId,
            requestId: requestId.current,
          }),
          signal, // Attach signal
        }
      );
  
      const orderData = await response.json();
  
      const transaction =
        orderData?.purchase_units?.[0]?.payments?.captures?.[0] ||
        orderData?.purchase_units?.[0]?.payments?.authorizations?.[0];
  
      if (!transaction || transaction.status === "DECLINED") {
        const errorMessage = transaction
          ? `Transaction ${transaction.status}: ${transaction.id}`
          : "Transaction failed. No details available.";
        alert(errorMessage);
        setMessage(errorMessage)
        throw new Error(errorMessage);
      }
  
      console.log("Capture result", orderData);
      navigate("/paypal-success");
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error on approval:", error);
        // alert(error.message || "An error occurred during payment approval.");
        setMessage(error?.message)
      }
    }
  
    // Clean up on unmount
    return () => {
      controller.abort();
    };
  };
  

  return (
    <div className="card_container">
      <PayPalScriptProvider options={initialOptions}>
        <div className="payment-section">
          <h2>PayPal Payment</h2>
          <PayPalButtons
            createOrder={createOrder}
            onApprove={async (data) => setMessage(await onApprove(data))}
            onError={(err) => console.error("PayPal Buttons Error:", err)}
            style={{ shape: "rect", layout: "vertical", color: "gold" }}
          />
        </div>

        <div className="card-payment-section">
          <h2>Card Payment</h2>
          <PayPalCardFieldsProvider
            createOrder={createOrder}
            onApprove={async (data) => setMessage(await onApprove(data))}
            onError={(err) => console.error("Card Fields Error:", err)}
          >
            <PayPalCardFieldsForm />
            <SubmitPayment isPaying={isPaying} setIsPaying={setIsPaying} setMessage={setMessage} />
          </PayPalCardFieldsProvider>
        </div>
      </PayPalScriptProvider>

      {message && (
        <div className="message">
          <Message content={message} />
        </div>
      )}
    </div>
  );
}

const SubmitPayment = ({ isPaying, setIsPaying,setMessage }) => {
  const { cardFieldsForm } = usePayPalCardFields();

  const handleClick = async () => {
    if (!cardFieldsForm) {
      console.error("Card fields form is not initialized.");
      return;
    }

    const formState = await cardFieldsForm.getState();
    if (!formState.isFormValid) {
      alert("Please fill in all required fields correctly.");
      return;
    }

    setIsPaying(true);
    try {
      await cardFieldsForm.submit();
    } catch (error) {
      console.error("Error during card submission:", error);
      setMessage(error?.message)
      // alert("Payment failed. Please try again.");
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <button
      className={`btn ${isPaying ? "btn-loading" : "btn-primary"}`}
      onClick={handleClick}
      disabled={isPaying}
    >
      {isPaying ? "Processing..." : "Pay Now"}
    </button>
  );
};

const Message = ({ content }) => (
  <div className="message-text">
    <p>{content}</p>
  </div>
);
